import { CSSObject, DefaultTheme, FlattenInterpolation, FlattenSimpleInterpolation, ThemeProps } from 'styled-components';

import { VALID_BREAKPOINTS } from '../../../lib/StyledComponents';
import {
  DeepPartial,
  VALID_BORDER_DATA,
  VALID_BORDER_RADIUS_DATA,
  VALID_COLOR_DATA,
  VALID_DIMENSION_DATA,
  VALID_EDGE_STYLE_DATA,
  VALID_FONT_SIZE_DATA,
  VALID_MEDIA_STYLES,
  VALID_OUTLINE_DATA,
  VALID_OUTLINE_STYLES,
  VALID_SHADOW_DATA,
} from '../Utils';
import IButton from '../Button/common';
import { IHeadingDefaultStyles, VALID_HEADING_LEVELS } from '../Typography/Headings/common';
import { IBannerTheme, VALID_BANNER_VARIANTS } from '../Banner/common';
import { IDatePicker } from '../DatePicker/common';
import {
  IRadioBadgeCommonTheme,
  IRadioButtonCommonTheme,
  IRadioCardCommonTheme,
  RadioButtonCheckedStyles
} from '../Form/RadioInputs/common';
import { VALID_FONT_WEIGHTS, VALID_GLOBAL_FONT_SIZES } from '../Typography/common';
import { ICheckboxCommonTheme } from '../Form/CheckboxInput/InputCheckboxField';
import { IInputFocusPosition } from '../Form/common';
import { IInputCommonTheme } from '../Form/TextInput/common';
import { LoadingSpinnerTheme } from '../Spinner/common';
import { IHeaderIconTheme, IHeaderTheme } from '../Header/common';
import { ITextProps } from '../Typography/Text/Text';
import { VALID_ICONS } from '../../Icon/SVGIcon';
import { IToggleCommonStateTheme } from '../Form/RadioInputs/RadioToggle/common';
import { VALID_FONTS } from '../../../lib/StyledComponents/Theme/common';
import { VALID_TEXT_SIZES } from '../Typography/Text/CoreText';
import { IPillTextCommon, ITextCommonCore } from '../Typography/Text/common';
import { IIDescriptionContentTheme } from '../DescriptionList/DescriptionGroup';
import { INotificationTheme } from '../Notification/Notification.type';
import { VALID_DIMENSION } from '../Utils/Styles/calcDimensionStyles';
import { IBorderRadiusData } from '../Utils/Styles/calcBorderRadiusStyles';
import { IHeaderLinkCommonTheme } from '../Header/Link/common';
import { VALID_PLACEMENTS } from '../Header/Dropdown/Drawer/StyledHeaderDrawer';
import { IProgressTrackerTheme } from '../ProgressTracker/common';
import { IFooterTheme } from '../Footer/common';
import { IFooterLinkTheme } from '../Footer/Link/common';

export const requiredColors = [
  'black',
  'error',
  'focus',
  'info',
  'link',
  'primary',
  'secondary',
  'success',
  'warning',
  'white'
];

export type VALID_Z_INDICES =
  | 'modal'
  | 'highest'
  | 'higher'
  | 'high'
  | 'middle'
  | 'low'
  | 'lower'
  | 'lowest';

type VALID_REQUIRED_COLORS = typeof requiredColors[number];
type AdditionalStyles = string | FlattenInterpolation<ThemeProps<DefaultTheme>> | FlattenSimpleInterpolation | CSSObject;

export type VALID_BREAKPOINTS_WITH_BASE = VALID_BREAKPOINTS | 'base';
export type RequireIndices = Record<VALID_Z_INDICES, string>;

type RequiredColors = Record<VALID_REQUIRED_COLORS, string>;
type UnrestrictedRecord = Record<string, string>;

// tslint:disable no-reserved-keywords
interface IThemeInterface {
  name: string;

  global: {
    breakPointSizes: VALID_BREAKPOINTS_WITH_BASE[];

    colors: RequiredColors & UnrestrictedRecord;

    edgeSize: UnrestrictedRecord & {
      none: string;
      single: string;
      double: string;
      quadruple: string;
    };

    elevation: Record<string, VALID_SHADOW_DATA> & Partial<{
      '1x': VALID_SHADOW_DATA;
      '2x': VALID_SHADOW_DATA;
      '3x': VALID_SHADOW_DATA;
      /* ....more shadows ex: 4x, ....etc*/
    }>;

    font: {
      family: Record<string, VALID_FONTS>;
      size: Record<VALID_GLOBAL_FONT_SIZES, string>;
    };

    outline: {
      color: string;
      size: string;
      style: VALID_OUTLINE_STYLES;
    };

    zIndex: RequireIndices & UnrestrictedRecord;

    // zIndex: UnrestrictedRecord;
  };

  banner: Partial<Record<VALID_BANNER_VARIANTS, DeepPartial<IBannerTheme>>> & {
    info: IBannerTheme;
  };

  barProgress: {
    borderRadius: VALID_BORDER_RADIUS_DATA;
    track: {
      height: VALID_DIMENSION_DATA;
      color: {
        base: string;
        progress: string;
      };
    };
  };

  button: IButton;

  circleProgress: {
    strokeWidth: number;
    backgroundPathColor: VALID_COLOR_DATA;
    percentagePathColor: VALID_COLOR_DATA;
    label: {
      percentage: {
        sign: ITextProps;
        number: ITextProps;
      };
      content: ITextProps;
    };
  };

  containerSize: UnrestrictedRecord & {
    full: '100%';
    narrow: string;
    /* ....more containers ex: narrow-2, ....etc */
    wide: string;
    /* ....more containers ex: wide-2, ....etc */
  };

  datePicker: IDatePicker;

  descriptionList: {
    group: {
      margin: VALID_EDGE_STYLE_DATA;
    };

    term: IIDescriptionContentTheme & {
      margin: VALID_EDGE_STYLE_DATA;
    };

    detail: IIDescriptionContentTheme;

    variant: {
      large: {
        term: IIDescriptionContentTheme & {
          margin: VALID_EDGE_STYLE_DATA;
        };
        detail: IIDescriptionContentTheme;
      };
      small: {
        term: IIDescriptionContentTheme & {
          margin: VALID_EDGE_STYLE_DATA;
        };
        detail: IIDescriptionContentTheme;
      };
    };
  };

  form: {
    autoComplete: {
      list: {
        shadow: VALID_SHADOW_DATA;
        backgroundColor: VALID_COLOR_DATA;
        height: { max: VALID_DIMENSION };
      };

      listItem: {
        padding: { top: string; bottom: string; horizontal?: string };
        margin: { top: string; bottom: string };
        font: {
          size: VALID_FONT_SIZE_DATA;
          color: VALID_COLOR_DATA;
          lineHeight: string;
        };

        active: {
          backgroundColor: VALID_COLOR_DATA;
          font: { color: VALID_COLOR_DATA };
        };
      };

      listItemHeader: {
        font: {
          size: VALID_FONT_SIZE_DATA;
          color: VALID_COLOR_DATA;
          weight: VALID_FONT_WEIGHTS;
          lineHeight: string;
        };
      };
    };

    checkbox: ICheckboxCommonTheme & {
      size: string;
      round: VALID_BORDER_RADIUS_DATA;

      state: {
        checked: Partial<ICheckboxCommonTheme>;
        checkedHover?: Partial<ICheckboxCommonTheme>;
        disabled: Partial<ICheckboxCommonTheme>;
        disabledChecked: Partial<ICheckboxCommonTheme>;
        error: Partial<ICheckboxCommonTheme>;
        focus: Partial<ICheckboxCommonTheme> & IInputFocusPosition;
        hover: Partial<ICheckboxCommonTheme>;
      };
    };

    layout: {
      wrapper: {
        row: {
          margin: { bottom: string };
        };
        stackRow: {
          margin: {
            [x in VALID_BREAKPOINTS_WITH_BASE]?: { right: string };
          };
        };
      };
    };

    errorText: {
      checkboxAndRadioErrorIndention?: boolean;

      font: {
        color: VALID_COLOR_DATA;
        size: VALID_FONT_SIZE_DATA;
        family?: VALID_FONTS;
        lineHeight?: string;
      };
      margin: VALID_EDGE_STYLE_DATA;
    };

    helperText: {
      font: {
        color: {
          default: VALID_COLOR_DATA;
          disabled: VALID_COLOR_DATA;
          error?: VALID_COLOR_DATA;
          hover?: VALID_COLOR_DATA;
          readOnly: VALID_COLOR_DATA;
        };
        lineHeight: string;
        size: VALID_FONT_SIZE_DATA;
        weight: VALID_FONT_WEIGHTS;
        family?: VALID_FONTS;
      };
      margin: { top: string };
    };

    input: IInputCommonTheme & {
      round: VALID_BORDER_RADIUS_DATA; // border-radius

      state: {
        hover: Partial<IInputCommonTheme & {
          shadow: VALID_SHADOW_DATA;
          outline: VALID_OUTLINE_DATA;
        }>;

        focus: Partial<IInputCommonTheme & {
          shadow: VALID_SHADOW_DATA;
          outline: VALID_OUTLINE_DATA;
        }>;

        error: Partial<IInputCommonTheme & {
          shadow: VALID_SHADOW_DATA;
        }>;

        disabled: Partial<IInputCommonTheme>;
        readOnly: Partial<IInputCommonTheme>;
      };
    };

    label: {
      font: {
        color: {
          default: VALID_COLOR_DATA;
          disabled: VALID_COLOR_DATA;
          error?: VALID_COLOR_DATA;
          readOnly: VALID_COLOR_DATA;
          hover?: VALID_COLOR_DATA;
        };
        size: VALID_FONT_SIZE_DATA;
        weight: VALID_FONT_WEIGHTS;
        family?: VALID_FONTS;
        lineHeight?: string;
      };

      margin: { // can assume margin will only be placed on bottom
        bottom: string;
      };

      additionalStyles?: AdditionalStyles;
    };

    legend: {
      font: {
        size: VALID_FONT_SIZE_DATA;
        color: VALID_COLOR_DATA;
        weight: string;
        family?: VALID_FONTS;
        lineHeight?: string;
      };

      margin: VALID_EDGE_STYLE_DATA;
    };

    radio: {
      button: RadioButtonCheckedStyles & IRadioButtonCommonTheme & {
        backgroundColor: VALID_COLOR_DATA;
        size: string;

        state: {
          checked: Partial<IRadioButtonCommonTheme>;
          checkedHover?: Partial<IRadioButtonCommonTheme>;
          disabled: Partial<IRadioButtonCommonTheme>;
          disabledChecked: Partial<IRadioButtonCommonTheme>;
          error: Partial<IRadioButtonCommonTheme>;
          focus: Partial<IRadioButtonCommonTheme> & IInputFocusPosition;
          hover: Partial<IRadioButtonCommonTheme>;
        };
      };

      card: IRadioCardCommonTheme & {
        border: VALID_BORDER_DATA;
        round: VALID_BORDER_RADIUS_DATA;
        shadow: VALID_SHADOW_DATA;

        state: {
          checked: Partial<IRadioCardCommonTheme>;
          disabled?: Partial<IRadioCardCommonTheme>;
          focus: Partial<IRadioCardCommonTheme>;
          hover: Partial<IRadioCardCommonTheme>;
          error: Partial<IRadioCardCommonTheme> & { text: {margin: { top: string }}};
        };
      };

      // badge is a text only radio card
      badge: IRadioBadgeCommonTheme & {
        padding: VALID_EDGE_STYLE_DATA;
        round: VALID_BORDER_RADIUS_DATA;
        width: VALID_DIMENSION_DATA;

        state: {
          checked: Partial<IRadioBadgeCommonTheme>;
          checkedHover: Partial<IRadioBadgeCommonTheme>;
          disabled: Partial<IRadioBadgeCommonTheme>;
          disabledChecked: Partial<IRadioBadgeCommonTheme>;
          error: Partial<IRadioBadgeCommonTheme>;
          focus: Partial<IRadioBadgeCommonTheme>;
          hover: Partial<IRadioBadgeCommonTheme>;
        };
      };

      toggle: IInputFocusPosition & {
        label: {
          color: VALID_COLOR_DATA;
          size: VALID_FONT_SIZE_DATA;
          weight: VALID_FONT_WEIGHTS;
          lineHeight: string;
        };

        button: {
          height: string;
          width: string;
          margin: { right: string };
          padding: { vertical: string; horizontal: string };
          border: VALID_BORDER_DATA;
          backgroundColor: VALID_COLOR_DATA;
          outline: VALID_OUTLINE_DATA | VALID_SHADOW_DATA;
          additionalStyles: AdditionalStyles;
        };

        slidingIndicator: {
          size: string;
          color: VALID_COLOR_DATA;
        };

        state: {
          checked: IToggleCommonStateTheme;
          checkedHover: IToggleCommonStateTheme;
          disabled: IToggleCommonStateTheme;
          disabledChecked: IToggleCommonStateTheme;
          focus: IToggleCommonStateTheme;
          hover: IToggleCommonStateTheme;
        };
      };
    };

    select: {
      icon: {
        type: VALID_ICONS;
        size: string;
        color?: VALID_COLOR_DATA;
        errorColor?: string;
      };
    };

    slider: {
      outline: VALID_OUTLINE_DATA;
      thumb: {
        color: {
          default: string;
          disabled: string;
        };
        height: number;
        width: number;
      };
      track: {
        color: {
          base: {
            default: string;
            disabled: string;
          };
          progress: {
            default: string;
            disabled: string;
          };
        };
        thickness: number;
        borderRadius: VALID_BORDER_RADIUS_DATA;
      };
      unitLabels: {
        color: {
          default: VALID_COLOR_DATA;
          disabled: VALID_COLOR_DATA;
        };
        margin?: { top: string };
      };
    };

    textarea: IInputCommonTheme & {
      round: VALID_BORDER_RADIUS_DATA; // border-radius

      state: {
        hover: Partial<IInputCommonTheme & {
          shadow: VALID_SHADOW_DATA;
          outline: VALID_OUTLINE_DATA;
        }>;

        focus: Partial<IInputCommonTheme & {
          shadow: VALID_SHADOW_DATA;
          outline: VALID_OUTLINE_DATA;
        }>;

        error: Partial<IInputCommonTheme & {
          shadow: VALID_SHADOW_DATA;
          outline: VALID_OUTLINE_DATA;
        }>;

        disabled: Partial<IInputCommonTheme & {
          shadow: VALID_SHADOW_DATA;
          outline: VALID_OUTLINE_DATA;
        }>;

        readOnly: Partial<IInputCommonTheme>;
      };
    };

  };

  heading: {
    default: IHeadingDefaultStyles;
    level: {
      [x in VALID_HEADING_LEVELS]: Partial<IHeadingDefaultStyles>;
    };
  };

  layout: {
    header: IHeaderTheme & {
      margin: { top: string };

      link: IHeaderLinkCommonTheme;

      dropdown: {
        alignItems: VALID_MEDIA_STYLES;
        backgroundColor: VALID_COLOR_DATA;
        shadow: VALID_SHADOW_DATA;
        border: VALID_BORDER_DATA;
        borderRadius: VALID_BORDER_RADIUS_DATA;
        width: VALID_DIMENSION_DATA;
        icon: {
          desktop: IHeaderIconTheme;
          mobile?: {
            open: IHeaderIconTheme;
            close: IHeaderIconTheme;
          };
        };
        padding: VALID_EDGE_STYLE_DATA;

        button: IHeaderLinkCommonTheme;

        link: IHeaderLinkCommonTheme & {
          divider?: {
            placement: VALID_PLACEMENTS;
            height: VALID_DIMENSION_DATA;
            backgroundColor: VALID_COLOR_DATA;
            margin?: VALID_EDGE_STYLE_DATA;
            // mobile is typically 100% | small is 100% - (padding left + padding right)
            width: VALID_DIMENSION_DATA;
          };
        };
      };
    };
    footer: IFooterTheme & {
      link: IFooterLinkTheme;
    };
  };

  link: {
    font: { color: string };
    inline: {
      state: {
        hover: { color: string };
      };
    };
  };

  list: {
    margin: VALID_EDGE_STYLE_DATA;

    listItem: {
      common: {
        margin: { bottom: string };
        font: {
          family: VALID_FONTS;
          lineHeight: string;
          size: VALID_GLOBAL_FONT_SIZES;
          color: VALID_COLOR_DATA;
          weight: VALID_FONT_WEIGHTS;
        };
      };

      bullet: {
        listStyleSpacing: string;
        size: string;
        backgroundColor: VALID_COLOR_DATA;
      };

      number: {
        listStyleSpacing: string;
        font: {
          weight: VALID_FONT_WEIGHTS;
          color: VALID_COLOR_DATA;
        };
      };

      icon: {
        listStyleSpacing: string;
        type: VALID_ICONS;
        size: string;
        color: VALID_COLOR_DATA;
      };
    };
  };

  modal: {
    overlay: {
      backgroundColor: VALID_COLOR_DATA;
      padding: VALID_EDGE_STYLE_DATA;
    };
    card: {
      backgroundColor: VALID_COLOR_DATA;
      border: VALID_BORDER_DATA;
      padding: VALID_EDGE_STYLE_DATA;
      round: VALID_BORDER_RADIUS_DATA;
      shadow: VALID_SHADOW_DATA;
    };
    closeIcon: {
      type: VALID_ICONS;
      size: string;
      color: string;
      padding: VALID_EDGE_STYLE_DATA;
    };
  };

  notification?: {
    variant: {
      [index: string]: INotificationTheme;
      success: INotificationTheme;
      error: INotificationTheme;
    };
    closeIcon?: {
      size: string;
      color: string;
      padding: VALID_EDGE_STYLE_DATA;
    };
  };

  progressTracker: {
    desktop: IProgressTrackerTheme;
    mobile: {
      stepCounter: ITextProps;
      stepName: ITextProps;
      upNext: ITextProps;
      nextStep: ITextProps;
    };
  };

  spinner: LoadingSpinnerTheme;

  table: {
    backgroundColor: VALID_COLOR_DATA;
    border: VALID_BORDER_DATA;
    borderSpacing: string;
    padding: VALID_EDGE_STYLE_DATA;
    font: {
      textIndent: string;
    };

    tableRow: {
      height: string;
      borderGutter: string;
    };

    tableHeader: {
      backgroundColor: VALID_COLOR_DATA;
      rowDivider: VALID_COLOR_DATA;
      padding: VALID_EDGE_STYLE_DATA;
      font: {
        size: VALID_GLOBAL_FONT_SIZES;
        color: VALID_COLOR_DATA;
        lineHeight: string;
        weight: VALID_FONT_WEIGHTS;
        textTransform: string;
      };
    };

    subHeader: {
      backgroundColor: VALID_COLOR_DATA;
      border: VALID_BORDER_DATA;
      font: {
        size: VALID_GLOBAL_FONT_SIZES;
        color: VALID_COLOR_DATA;
        weight: VALID_FONT_WEIGHTS;
      };
    };

    tableBody: {
      backgroundColor: VALID_COLOR_DATA;
      rowDivider: VALID_COLOR_DATA;
      borderRadius: IBorderRadiusData;
      boxShadow: VALID_SHADOW_DATA;
      padding: VALID_EDGE_STYLE_DATA;
      font: {
        size: VALID_GLOBAL_FONT_SIZES;
        color: VALID_COLOR_DATA;
        lineHeight: string;
        weight: VALID_FONT_WEIGHTS;
      };
    };
  };

  tooltip: {
    activationType: 'automatic' | 'manual';
    pane: {
      background: string;
      padding: VALID_EDGE_STYLE_DATA;
      shadow: VALID_SHADOW_DATA;
      round: VALID_BORDER_RADIUS_DATA;
      verticalOffset?: string;
      helperTextVerticalOffset?: string;
      border?: VALID_BORDER_DATA;
      margin?: VALID_EDGE_STYLE_DATA;
      position: 'top' | 'bottom';

      font: {
        size: VALID_FONT_SIZE_DATA;
        color: string;
        family?: VALID_FONTS;
        lineHeight: string;
      };
    };

    icon: {
      size: string;
      color: string;
      hover: string; // hover color
      margin: VALID_EDGE_STYLE_DATA;
    };
  };

  typography: {
    text: {
      // hero variant pulls from headings
      body: {
        default: ITextCommonCore;
      } & Partial<Record<VALID_TEXT_SIZES, Partial<ITextCommonCore>>>;
    };

    pill: {
      default: IPillTextCommon;
      [index: string]: DeepPartial<IPillTextCommon>;
    };
  };

  // this is a place for application level theming to avoid name space issues
  // tslint:disable-next-line: no-any
  application?: Record<any, any>;
}

export default IThemeInterface;

export const zIndices: RequireIndices = {
  modal: '999999999999',
  highest: '5000',
  higher: '4000',
  high: '3000',
  middle: '2000',
  low: '1000',
  lower: '-1000',
  lowest: '-2000',
};

export const edgeSize = {
  none: '0rem',
  single: '0.1rem', // 1px
  double: '0.2rem', // 2px
  triple: '0.3rem', // 3px
  quadruple: '0.4rem', // 4px
  '0.625x': '0.5rem', // 5px
  '0.75x': '0.6rem', // 6px
  '0.875x': '0.7rem', // 7px
  // values are set based on a 8pt scale
  '1x': '0.8rem', // 8px
  '1.0625x': '0.85rem', // 8.5px
  '1.125x': '0.9rem', // 9px
  '1.25x': '1rem', // 10px
  '1.5x': '1.2rem', // 12px
  '1.75x': '1.4rem', // 14px
  '1.875x': '1.5rem', // 15px
  '2x': '1.6rem', // 16px
  '2.25x': '1.8rem', // 18px
  '2.5x': '2rem', // 20px
  '2.75x': '2.2rem', // 22px
  '3x': '2.4rem', // 24px
  '3.5x': '2.8rem', // 28px
  '4x': '3.2rem', // 32px
  '4.5x': '3.6rem', // 36px
  '5x': '4rem', // 40px
  '6x': '4.8rem', // 48px
  '7x': '5.6rem', // 56px
  '7.5x': '6rem', // 60px
  '8x': '6.4rem', // 64px
  '9x': '7.2rem', // 72px
  '10x': '8rem', // 80px
  '11x': '8.8rem', // 88px
  '12x': '9.6rem', // 96px
};

export const breakPointSizes: VALID_BREAKPOINTS_WITH_BASE[] =
  ['base', 'xsmall', 'small', 'medium', 'mediumLarge', 'large', 'xlarge', 'xxlarge'];
